class ProductRecommendations extends HTMLElement {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  static #styleSheet = null;

  connectedCallback() {
    this.#injectStyles();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  /**
   * Injects the styles for the product recommendations
   */
  #injectStyles() {
    if (ProductRecommendations.#styleSheet) return;

    ProductRecommendations.#styleSheet = document.createElement('style');
    ProductRecommendations.#styleSheet.textContent = `
      .product-recommendations product-price,
      .product-recommendations .product-card-price-container .price,
      .product-recommendations .product-card-price-container .compare-at-price,
      .product-recommendations .product-card-price-container .savings-text,
      .product-recommendations .product-card-price-container .loyalty-badge {
        text-align: left !important;
        justify-content: flex-start !important;
        align-items: flex-start !important;
      }

      .product-recommendations
        .product-card-price-container.product-card-price-container--on-sale:not(
          .product-card-price-container--loyalty
        ) {
        display: flex !important;
        flex-direction: row !important;
        justify-content: flex-start !important;
        align-items: center !important;
        gap: 8px !important;
        align-self: stretch !important;
      }

      .product-recommendations
        .product-card-price-container.product-card-price-container--on-sale:not(
          .product-card-price-container--loyalty
        )
        .price {
        color: var(--Neutral-90, #14181C) !important;
        font-size: 18px !important;
        font-style: normal !important;
        font-weight: 500 !important;
        line-height: 150% !important;
        text-align: left !important;
      }

      .product-recommendations
        .product-card-price-container.product-card-price-container--on-sale:not(
          .product-card-price-container--loyalty
        )
        .compare-at-price {
        color: var(--Neutral-60, #81878B) !important;
        font-size: 14px !important;
        font-style: normal !important;
        font-weight: 400 !important;
        line-height: 150% !important;
        opacity: 1 !important;
        text-decoration-line: line-through !important;
        text-align: left !important;
      }

      .product-recommendations
        .product-card-price-container.product-card-price-container--on-sale:not(
          .product-card-price-container--loyalty
        )
        .savings-text {
        display: none !important;
      }

      .product-recommendations .product-card-price-container.product-card-price-container--loyalty {
        display: flex !important;
        flex-direction: column !important;
        align-items: flex-start !important;
        gap: 8px !important;
        align-self: stretch !important;
      }

      .product-recommendations .product-card-price-container.product-card-price-container--loyalty .loyalty-badge {
        display: flex !important;
        align-items: center !important;
        justify-content: flex-start !important;
        gap: 8px !important;
        align-self: stretch !important;
        margin-bottom: 0 !important;
      }

      .product-recommendations .product-card-price-container.product-card-price-container--loyalty .loyalty-badge__icon {
        width: 24px !important;
        height: 24px !important;
        flex-shrink: 0 !important;
        aspect-ratio: 1 / 1 !important;
      }

      .product-recommendations
        .product-card-price-container.product-card-price-container--loyalty
        .loyalty-badge__icon
        svg {
        width: 24px !important;
        height: 24px !important;
        display: block !important;
      }

      .product-recommendations .product-card-price-container.product-card-price-container--loyalty .loyalty-badge__text {
        color: var(--Neutral-90, #14181C) !important;
        font-size: 14px !important;
        font-style: normal !important;
        font-weight: 400 !important;
        line-height: 150% !important;
      }

      .product-recommendations .product-card-price-container.product-card-price-container--loyalty .loyalty-badge__text strong {
        font-weight: 500 !important;
      }

      .product-recommendations
        .product-card-price-container.product-card-price-container--loyalty
        .product-card-loyalty-price-row {
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
        gap: 12px !important;
      }

      .product-recommendations
        .product-card-price-container.product-card-price-container--loyalty
        .product-card-loyalty-price-row
        .price {
        color: var(--Neutral-90, #14181C) !important;
        font-size: 18px !important;
        font-style: normal !important;
        font-weight: 500 !important;
        line-height: 150% !important;
      }

      .product-recommendations
        .product-card-price-container.product-card-price-container--loyalty
        .product-card-loyalty-price-row
        .compare-at-price {
        color: var(--Neutral-60, #81878B) !important;
        font-size: 14px !important;
        font-style: normal !important;
        font-weight: 400 !important;
        line-height: 150% !important;
        opacity: 1 !important;
        text-decoration-line: line-through !important;
      }

      .product-recommendations .product-card-price-container.product-card-price-container--loyalty > .price,
      .product-recommendations .product-card-price-container.product-card-price-container--loyalty > .compare-at-price,
      .product-recommendations .product-card-price-container.product-card-price-container--loyalty .savings-text {
        display: none !important;
      }

      .product-recommendations .reply-loyalty-pricing-wrapper--collection {
        display: flex !important;
        flex-direction: column !important;
        align-items: flex-start !important;
        gap: 8px !important;
        align-self: stretch !important;
        text-align: left !important;
      }

      .product-recommendations .reply-loyalty-pricing-qualify-info {
        display: flex !important;
        align-items: center !important;
        justify-content: flex-start !important;
        gap: 8px !important;
        padding-bottom: 0 !important;
      }

      .product-recommendations .reply-loyalty-pricing-qualify-icon {
        width: 24px !important;
        height: 24px !important;
        flex-shrink: 0 !important;
        aspect-ratio: 1 / 1 !important;
      }

      .product-recommendations .reply-loyalty-pricing-qualify-icon svg {
        width: 24px !important;
        height: 24px !important;
        display: block !important;
      }

      .product-recommendations .reply-loyalty-pricing-qualify-text {
        color: var(--Neutral-90, #14181C) !important;
        font-size: 14px !important;
        font-style: normal !important;
        font-weight: 400 !important;
        line-height: 150% !important;
        margin: 0 !important;
        text-align: left !important;
      }

      .product-recommendations .reply-loyalty-pricing-bold {
        font-weight: 500 !important;
      }

      .product-recommendations .reply-loyalty-pricing-wrapper--collection .reply-loyalty-pricing-price-container {
        width: 100% !important;
        margin-top: 0 !important;
        display: flex !important;
        justify-content: flex-start !important;
      }

      .product-recommendations .reply-loyalty-pricing-wrapper--collection .reply-loyalty-pricing-price-block {
        display: flex !important;
        flex-direction: row !important;
        align-items: center !important;
        gap: 12px !important;
      }

      .product-recommendations .reply-loyalty-pricing-wrapper--collection .reply-loyalty-pricing-current-price {
        color: var(--Neutral-90, #14181C) !important;
        font-size: 18px !important;
        font-style: normal !important;
        font-weight: 500 !important;
        line-height: 150% !important;
      }

      .product-recommendations .reply-loyalty-pricing-wrapper--collection .reply-loyalty-pricing-compare-container {
        display: flex !important;
        flex-direction: row !important;
        align-items: center !important;
        gap: 0 !important;
      }

      .product-recommendations .reply-loyalty-pricing-wrapper--collection .reply-loyalty-pricing-compare-price {
        color: var(--Neutral-60, #81878B) !important;
        font-size: 14px !important;
        font-style: normal !important;
        font-weight: 400 !important;
        line-height: 150% !important;
        opacity: 1 !important;
        text-decoration-line: line-through !important;
      }

      .product-recommendations .reply-loyalty-pricing-wrapper--collection .reply-loyalty-pricing-savings {
        display: none !important;
      }
    `;
    document.head.appendChild(ProductRecommendations.#styleSheet);
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
